import {Form, Spin} from 'antd';
import v2 from '../../v2/instrumentV2.module.scss';
import {InstrumentActions} from '../../v2/InstrumentActions';
import {
  OfferingFields,
  buildOffering,
  offeringToFormValues,
} from '../../v2/OfferingFields';
import {BOND_OFFERING_MOCK} from '../../v2/devPrefill';
import {useMutation, useQuery} from '@apollo/client';
import {useDispatch, useSelector} from 'react-redux';
import {handleBondsInstrumentState} from 'redux/store/assetClass';
import {useEffect, useState} from 'react';
import {CreateBondFinishModal} from '../CreateBondFinishModal';
import DBModal from 'components/modal/modal';
import {useParams} from 'react-router-dom';
import {UPDATE_BOND} from 'graphql/mutations/assetManagement';
import {GET_BOND_INSTRUMENT_BY_LANGUAGE} from 'graphql/queries/assetManagement';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {handleUpdateBond} from 'pages/AssetManagement/actions';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';

// Everything on this screen lives under `offering` now. The five flat
// primaryBroker* fields became `offering.parties[]` — a repeatable list where
// each entry carries its own role — and useOfProceeds moved under offering too.
// `legalChallenges`, `outlookAsOfDate` and `creditWatchAsOfDate` were dropped
// by the FinancialInstrument migration with no replacement.
const BondsBrokerCompliance = () => {
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [createBondModal, setCreateBondModal] = useState(false);
  const {id, lang} = useParams();

  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const bondData = useSelector(state => state?.assetClass?.bondInstruments);
  const [updatedValues, setUpdatedValues] = useState({});

  const [updateBond, {loading: loadingUpdate}] = useMutation(UPDATE_BOND);
  const {
    data: fetched,
    refetch,
    loadingQuery,
  } = useQuery(GET_BOND_INSTRUMENT_BY_LANGUAGE, {
    variables: {
      getFinancialInstrumentId: id,
      language: lang,
    },
    skip: !id,
  });

  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.assetClasses),
  );

  const handleSubmit = values => {
    if (id) {
      setOpenUpdateModal(true);
      // `offering` sits on the base instrument input, not on UpdateBondInput,
      // so this screen writes through the base path rather than the step-two one.
      setUpdatedValues({offering: buildOffering(values)});
    } else {
      dispatch(
        handleBondsInstrumentState({
          offering: buildOffering(values),
        }),
      );
      setCreateBondModal(true);
    }
  };

  // Populate from the saved record when editing, and from the draft when
  // creating. The fetched result was previously ignored entirely, so an
  // existing bond's parties and allocations came back blank.
  const applyOffering = offering => {
    if (!offering) return;
    form.setFieldsValue(offeringToFormValues(offering));
  };

  useEffect(() => {
    if (id) applyOffering(fetched?.getFinancialInstrument?.data?.offering);
  }, [fetched?.getFinancialInstrument?.data]);

  useEffect(() => {
    if (!id && bondData) applyOffering(bondData?.offering);
  }, [bondData]);

  if (loadingQuery) {
    return (
      <div className="query-loading">
        <Spin size="large" />
        <h1>Loading...</h1>
      </div>
    );
  }

  return (
    <div>
      <div className={v2.bodyHead}>
        <h2>Offering parties</h2>
        <p>
          Who is bringing this issue to market, and how the proceeds will be
          used.
        </p>
      </div>

      <Form onFinish={handleSubmit} form={form} layout="vertical">
        <OfferingFields />

        <InstrumentActions
          onPrepopulate={() => form.setFieldsValue(BOND_OFFERING_MOCK)}
          disabled={!pageOperations?.update}
          label="Save and proceed"
        />
      </Form>
      <DBModal
        isOpen={createBondModal}
        handleClose={() => setCreateBondModal(false)}
        content={
          <CreateBondFinishModal
            handleClose={() => setCreateBondModal(false)}
          />
        }
      />
      <ConfirmModal
        openModal={openUpdateModal}
        setOpenModal={() => setOpenUpdateModal(false)}
        loading={loadingUpdate}
        title="Update Bond"
        confirmText="Are you sure you want to update this bond?"
        handleOperation={() =>
          handleUpdateBond(
            updateBond,
            id,
            lang,
            updatedValues,
            refetch,
            setOpenUpdateModal,
          )
        }
      />
    </div>
  );
};

export default BondsBrokerCompliance;
